<script setup lang="ts">
import { AlertType, ConfirmChoice } from '@/store/uiStore';
import { useUiStore } from '@/store/uiStore';

const props = withDefaults(
  defineProps<{
    kind?: AlertType;
    requiredAnswer?: string;
    resolve: (value: boolean | string) => void;
    title?: string;
    subtitle?: string;
    description?: string;
    yesText?: string;
    noText?: string;
    /** Named choices (keep/delete); replaces the yes button, Cancel stays */
    choices?: ConfirmChoice[];
  }>(),
  {
    kind: 'info',
    requiredAnswer: undefined,
    title: '',
    subtitle: '',
    description: '',
    yesText: '',
    noText: '',
    choices: undefined,
  },
);

const buttonVariant = computed(() => {
  if (props.kind === 'danger') {
    return 'error';
  }
  if (props.kind === 'warning') {
    return 'flat';
  }
  if (props.kind === 'success') {
    return 'primary';
  }

  return 'default';
});

const textAnswer = ref('');
const rootRef = ref<HTMLElement>();

useUiStore().addKeymap({
  Escape: () => {
    props.resolve(false);
  },
  // Enter takes the default choice of a choice dialog, and cancels one that
  // has none (WI #5795: a template's Delete / Cancel); plain confirms are
  // unaffected. A keyboard user who tabbed onto one of the dialog's buttons
  // gets that button's own Enter activation instead.
  Enter: () => {
    const active = document.activeElement;
    if (
      !props.choices?.length ||
      (active instanceof HTMLButtonElement && rootRef.value?.contains(active))
    ) {
      return;
    }
    const defaultChoice = props.choices.find((choice) => choice.isDefault);
    props.resolve(defaultChoice ? defaultChoice.value : false);
  },
});

function submit() {
  const answer = props.requiredAnswer ? textAnswer.value : true;
  props.resolve(answer);
}
</script>

<template>
  <div ref="rootRef" class="ui-confirm" :data-variant="kind">
    <span class="modal-title">{{ title || $t('button.confirm') + ' ?' }}</span>
    <span v-if="subtitle" class="modal-subtitle">{{ subtitle }}</span>
    <span v-if="description" class="modal-description">{{ description }}</span>
    <span v-if="description && requiredAnswer" class="">
      <b>{{ requiredAnswer }}</b>
    </span>

    <UiInput
      v-if="requiredAnswer"
      v-model="textAnswer"
      class="required-input"
    />

    <div v-if="choices?.length" class="button-row">
      <UiButton block expand @click="resolve(false)">
        {{ noText || $t('button.cancel') }}
      </UiButton>
      <UiButton
        v-for="choice in choices"
        :key="choice.value"
        :variant="choice.variant ?? 'default'"
        block
        expand
        @click="resolve(choice.value)"
      >
        {{ choice.text }}
      </UiButton>
    </div>
    <div v-else class="button-row">
      <UiButton block expand @click="resolve(false)">
        {{ noText || $t('button.cancel') }}
      </UiButton>
      <UiButton
        :variant="buttonVariant"
        :disabled="requiredAnswer ? textAnswer !== requiredAnswer : false"
        block
        expand
        @click="submit"
      >
        {{ yesText || $t('button.confirm') }}
      </UiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ui-confirm {
  padding: var(--space-24);
  border: 2px solid #ddd;
  border-radius: var(--border-radius-14);
  text-align: center;
  background-color: #f8f8f8;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: px(380);

  &[data-variant='info'] {
    border: var(--color-bluelight-500) 2px solid;
    color: var(--color-gray-700);
  }

  &[data-variant='success'] {
    border: var(--color-success-400) 2px solid;
    color: var(--color-gray-700);
  }

  &[data-variant='warning'] {
    border: var(--color-warning-500) 2px solid;
    color: var(--color-gray-700);
    flex-flow: row-reverse;
  }

  &[data-variant='danger'] {
    border: var(--color-error-600) 2px solid;
    color: var(--color-gray-700);
    flex-flow: row-reverse;
  }
}

.modal-title {
  display: block;
  width: 100%;
  margin-bottom: var(--space-20);
  font-size: var(--font-size-18);
  font-weight: var(--font-weight-bold);
}

.modal-subtitle {
  display: block;
  width: 100%;
  margin-bottom: var(--space-20);
}

.modal-description {
  display: block;
  width: 100%;
}

.required-input {
  display: block;
  width: 100%;
  margin-top: var(--space-20);
}

.button-row {
  display: flex;
  gap: var(--space-16);
  padding-top: var(--space-32);
  align-items: center;
  width: 100%;
}

// A destructive choice next to the primary one reads as outlined red rather
// than a second solid fill (the danger confirm's solid red is its only action).
.ui-confirm .button-row .ui-button.error {
  color: var(--color-error-600);
  border-color: var(--color-error-600);
  background-color: var(--color-gray-50);

  &:hover {
    background-color: var(--color-error-50);
  }
}
</style>
